import { calcMilestone, genAttributes } from '@onward-delivery/core';
import orderStatusStrings from '@/constants/orderStatusStrings';
import { PALLET_STATUSES } from '@/constants/manifestStatuses';

/**
 * Status text for an order in My Orders: "Order Split" for a split parent (core's milestone has no
 * split case), otherwise the order's milestone, or "-" when it has none. Shared by the Status
 * column and the CSV export.
 *
 * @param {object} order Order with the fields `calcMilestone` reads.
 * @returns {string}
 */
export const orderStatusText = (order) =>
    order?.order_status === 'split' ? orderStatusStrings.split : calcMilestone(order) || '-';

// The order's destination address, split the way the CSV importer names it: `street` is Address
// Line 1 and `unit` is Address Line 2 (generateOptions.js labels pickup_unit / customer_unit
// exactly that). Kept apart rather than joined so an exported file can be read back in.
//
// genAttributes resolves the side, so a return reads its pickup address the way the table's other
// Destination columns already do. Shared by the table columns and the export columns so the two
// can never disagree.
const destinationPart = (order, key) => {
    const attrs = genAttributes(order);
    return order[attrs[key]] || '-';
};

export const destinationAddress = (order) => destinationPart(order, 'street');
export const destinationAddress2 = (order) => destinationPart(order, 'unit');

// Columns offered in the export picker that have no visible table column behind them. `before`
// names the table column id the export column is slotted ahead of, so the CSV reads in a logical
// order; entries with no surviving anchor append in declaration order.
//
// Destination Address has a table column too, but it is hidden by default -- this is the fallback
// that keeps it exportable without first enabling it. buildExportColumnList drops an entry whose
// header a visible table column already contributes, so only ever one of the two is offered.
export const EXPORT_ONLY_COLUMNS = [
    {
        before: 'destination_city',
        column: { header: 'Destination Address', value: ({ order }) => destinationAddress(order) },
    },
    {
        before: 'destination_city',
        column: { header: 'Destination Address 2', value: ({ order }) => destinationAddress2(order) },
    },
    {
        before: 'received_date',
        column: { header: 'Bin/Pool Location', value: (row) => binPoolLocationExport(row) },
    },
];

/**
 * The hidden columns to apply from a saved table profile. A profile saves its full hidden list, so
 * a default-hidden column added after it was saved would show for everyone using it. This also
 * hides any default-hidden column the profile didn't know about. A user who shows such a column
 * and saves the profile again keeps it showing.
 *
 * @param {{ hiddenColumns?: string[], knownDefaultHidden?: string[] }} layout The profile's saved layout.
 *   `knownDefaultHidden` is the default-hidden list when the profile was saved.
 * @param {string[]} defaultHidden The table's default-hidden columns now.
 * @param {string[]} legacyKnownDefaultHidden What to assume a profile knew when it was saved before
 *   profiles recorded `knownDefaultHidden`.
 * @returns {string[]}
 */
export const profileHiddenColumns = (layout, defaultHidden, legacyKnownDefaultHidden) => {
    const saved = layout?.hiddenColumns;
    if (!saved) return defaultHidden;
    const known = new Set(layout.knownDefaultHidden || legacyKnownDefaultHidden);
    const unseen = defaultHidden.filter((id) => !known.has(id) && !saved.includes(id));
    return unseen.length ? [...saved, ...unseen] : saved;
};

export const flattenTableLeafColumns = (cols) =>
    (cols || []).flatMap((c) => (c?.columns?.length ? flattenTableLeafColumns(c.columns) : [c]));

/**
 * Builds the export picker's column list so the CSV mirrors the table the user is looking at:
 * the saved column order first, then any columns saved before those columns existed, with
 * hidden and non-exportable columns dropped. Export-only columns are spliced in ahead of their
 * `before` anchor, or appended when that anchor no longer exists.
 */
export const buildExportColumnList = ({
    leafIds = [],
    savedOrder = [],
    hiddenColumns = [],
    exportersById = {},
    exportOnlyColumns = [],
}) => {
    const available = new Set(leafIds);
    const hidden = new Set(hiddenColumns);

    const seen = new Set();
    const orderedIds = [];
    savedOrder.forEach((id) => {
        if (!seen.has(id) && available.has(id)) {
            orderedIds.push(id);
            seen.add(id);
        }
    });
    leafIds.forEach((id) => {
        if (!seen.has(id)) {
            orderedIds.push(id);
            seen.add(id);
        }
    });

    const isExportable = (id) => !hidden.has(id) && !!exportersById[id];

    // Anchors resolve against the whole column order rather than the visible subset, so an
    // export-only column keeps its slot when the column it sits beside is hidden. Anchoring on
    // visibility made one column's position depend on an unrelated column's.
    const anchors = new Set(orderedIds);

    // ExportCsvTemplateModal keys both `selectedColumns` and its React list by `column.header`, so
    // two entries sharing a header silently merge into one checkbox. The table column wins and the
    // export-only entry stands in only while that column is hidden.
    const visibleHeaders = new Set(orderedIds.filter(isExportable).map((id) => exportersById[id].header));
    const exportOnly = exportOnlyColumns.filter(({ column }) => !visibleHeaders.has(column?.header));

    const columns = orderedIds.flatMap((id) => [
        ...exportOnly.filter(({ before }) => before === id).map(({ column }) => column),
        ...(isExportable(id) ? [exportersById[id]] : []),
    ]);

    const unanchored = exportOnly.filter(({ before }) => !anchors.has(before)).map(({ column }) => column);

    return [...columns, ...unanchored];
};

/**
 * Splits a bulk-action selection into orders the action can run on and split parents, which hold no
 * items and are history, so bulk actions skip them. The database only rejects putting one on a route.
 *
 * @param {Array<object|null|undefined>} orders Selected orders.
 * @returns {{ eligible: object[], skippedSplit: object[] }}
 */
export const partitionSplitParents = (orders) => {
    const eligible = [];
    const skippedSplit = [];
    (orders || []).forEach((order) => {
        if (!order) return;
        (order.order_status === 'split' ? skippedSplit : eligible).push(order);
    });
    return { eligible, skippedSplit };
};

/**
 * Text for split parents a bulk action left out.
 *
 * @param {number} count How many split parents were skipped.
 * @returns {string}
 */
export const skippedSplitMessage = (count) =>
    `Skipped ${count} split order(s). Complete or receive the child orders instead.`;

/**
 * One skip notice for a bulk action, joining the pickup-order and split-parent parts that apply.
 *
 * @param {{ pickupCount?: number, splitCount?: number }} counts Orders skipped for each reason.
 * @returns {string} The combined text, or '' when nothing was skipped.
 */
export const bulkSkipMessage = ({ pickupCount = 0, splitCount = 0 } = {}) =>
    [
        pickupCount
            ? `Skipped ${pickupCount} selected order(s), these are pickup orders that cannot be marked received here.`
            : '',
        splitCount ? skippedSplitMessage(splitCount) : '',
    ]
        .filter(Boolean)
        .join(' ');

// A pallet still in the building. Its location means nothing once the freight is picked up, and a
// pallet with no status yet still sits where it was put.
const IN_WAREHOUSE_PALLET = {
    _or: [{ warehouse_status: { _is_null: true } }, { warehouse_status: { _neq: PALLET_STATUSES.PICKED_UP } }],
};

// Order fields the My Orders search bar matches. A string is an order column matched by substring.
// A `jsonKey` entry is an exact key inside a jsonb column. Item barcodes and bin/pool locations
// aren't here: they're looked up first (searchLookupWheres) and arrive as order ids, because an
// item or pallet condition inside the order filter is checked once per order and is several times
// slower for large clients.
const SEARCH_FIELDS = [
    'order_number',
    'po_number',
    'pickup_city',
    'pickup_state',
    'pickup_zip',
    'dropoff_name',
    'dropoff_city',
    'dropoff_state',
    'dropoff_zip',
    'reference_id',
    'dropoff_phone',
    'manufacturer',
    // Address Line 1 on both sides, so a return, whose destination is its pickup, is found too.
    'dropoff_address',
    'pickup_address',
    { jsonKey: ['middle_mile_integration_meta', 'tracking_number'] },
    { jsonKey: ['dispatch_integration_meta', 'tracking_number'] },
];

/**
 * The `where` clause for a My Orders search term: any order search field may match, or the order
 * is one of `matchedOrderIds` (orders whose item barcode or bin/pool matched, from the lookups).
 *
 * @param {string} term What the user typed.
 * @param {string[]} [matchedOrderIds] Order ids found by the item and pallet lookups.
 * @returns {object|null} A Hasura `_or` clause, or null when there is nothing to search.
 */
export const buildSearchWhere = (term, matchedOrderIds = []) => {
    const text = (term || '').trim();
    if (!text) return null;

    const ilike = { _ilike: `%${text}%` };
    const fields = SEARCH_FIELDS.map((field) => {
        if (typeof field === 'string') return { [field]: ilike };
        const [col, key] = field.jsonKey;
        return { [col]: { _contains: { [key]: text } } };
    });
    return { _or: matchedOrderIds.length ? [...fields, { order_id: { _in: matchedOrderIds } }] : fields };
};

/**
 * Who can see an order on My Orders: any of the viewer's clients in any of its client roles.
 *
 * @param {string[]} clientIds The viewer's client ids.
 * @returns {object} A Hasura `orders_bool_exp`.
 */
export const orderVisibilityWhere = (clientIds) => ({
    _or: ['shipper_id', 'carrier_id', 'middle_mile_carrier_id', 'warehouse_carrier_id', 'contractor_id'].map((col) => ({
        [col]: { _in: clientIds || [] },
    })),
});

/**
 * @typedef {object} OrderLookup
 * @property {object} where Its `items_bool_exp`.
 * @property {number|null} limit The most orders it returns, or null for every match. Exact filters
 *   (a scan, picked bins) are uncapped so no order is left out; search lookups only add matches.
 */

/** The most orders a search term's barcode or bin/pool lookup adds to the search. */
export const SEARCH_LOOKUP_LIMIT = 1000;

/**
 * The shortest search term that also looks up barcodes and bins. A trigram index can't serve a
 * shorter pattern, so the lookup scans items and makes the table wait on the first keystrokes.
 */
export const MIN_LOOKUP_TERM_LENGTH = 3;

/**
 * Item and pallet lookups for a search term: items whose barcode contains it, and items on pallets
 * still in the warehouse whose bin or pool contains it. Both are limited to orders the viewer can
 * see. Terms under MIN_LOOKUP_TERM_LENGTH characters skip them and search order fields only.
 *
 * @param {string} term What the user typed.
 * @param {object} visibility An `orders_bool_exp`, from orderVisibilityWhere.
 * @returns {{ barcode: OrderLookup, location: OrderLookup }|null} Null when there is nothing to look up.
 */
export const searchLookupWheres = (term, visibility) => {
    const text = (term || '').trim();
    if (text.length < MIN_LOOKUP_TERM_LENGTH) return null;

    const ilike = { _ilike: `%${text}%` };
    return {
        barcode: { where: { barcode: ilike, order: visibility }, limit: SEARCH_LOOKUP_LIMIT },
        location: {
            where: { pallet: { _and: [{ warehouse_location: ilike }, IN_WAREHOUSE_PALLET] }, order: visibility },
            limit: SEARCH_LOOKUP_LIMIT,
        },
    };
};

const binItemsWhere = (locationComparison, visibility, warehouseId) => ({
    pallet: {
        _and: [
            { warehouse_location: locationComparison },
            ...(warehouseId ? [{ warehouse_id: { _eq: warehouseId } }] : []),
            IN_WAREHOUSE_PALLET,
        ],
    },
    order: visibility,
});

/**
 * The lookup that finds the orders a scanned label belongs to.
 *
 * @param {ScanTarget} scan
 * @param {object} visibility An `orders_bool_exp`, from orderVisibilityWhere.
 * @returns {OrderLookup}
 */
export const scanLookupWhere = ({ type, value, warehouseId }, visibility) => {
    if (type === 'item') return { where: { item_id: { _eq: value }, order: visibility }, limit: null };
    if (type === 'pallet') return { where: { pallet_id: { _eq: value }, order: visibility }, limit: null };
    // Bin names like "Rack A-1-1-1" repeat across warehouses, so a label's warehouse narrows it.
    return { where: binItemsWhere({ _eq: value }, visibility, warehouseId), limit: null };
};

/**
 * The lookup that finds orders with freight still in any of the given bins or pools.
 *
 * @param {string[]} locations
 * @param {object} visibility An `orders_bool_exp`, from orderVisibilityWhere.
 * @returns {OrderLookup}
 */
export const binLocationsLookupWhere = (locations, visibility) => ({
    where: binItemsWhere({ _in: locations }, visibility),
    limit: null,
});

/**
 * Pallets still in the warehouse, with a bin or pool, that hold freight for orders the viewer can
 * see: the source of the Bin/Pool Location filter's options.
 *
 * @param {object} visibility An `orders_bool_exp`, from orderVisibilityWhere.
 * @returns {object} A Hasura `pallet_items_bool_exp`.
 */
export const binLocationOptionsWhere = (visibility) => ({
    _and: [
        { warehouse_location: { _is_null: false, _neq: '' } },
        IN_WAREHOUSE_PALLET,
        { items: { order: visibility } },
    ],
});

/**
 * The distinct order ids a lookup returned.
 *
 * @param {{ items?: Array<{ order_id?: string }> }|undefined} data A lookup query result.
 * @returns {string[]}
 */
export const orderIdsFromLookup = (data) => [
    ...new Set((data?.items || []).map((item) => item?.order_id).filter(Boolean)),
];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const validScan = (type, value) => {
    if (typeof value !== 'string' || !value.trim()) return false;
    if (type === 'item' || type === 'pallet') return UUID_RE.test(value);
    return type === 'bin';
};

/**
 * @typedef {object} ScanTarget
 * @property {'item'|'pallet'|'bin'} type Which kind of Onward label was scanned.
 * @property {string} value The item id, pallet id, or bin label the label carries.
 * @property {string} [warehouseId] For a bin label, the warehouse it was printed for.
 */

/**
 * Reads an Onward QR label payload, as a keyboard-wedge scanner types it into the search bar.
 * The format is written by onward-core's `labels/qr.js` (item and pallet) and the web bin-label
 * printer: `{"labelType":"item","value":"<item_id>"}`.
 *
 * @param {string} text Search bar contents.
 * @returns {ScanTarget|null} The label, or null when the text is not an Onward label.
 */
export const parseScanPayload = (text) => {
    const raw = (text || '').trim();
    if (!raw.startsWith('{') || !raw.endsWith('}')) return null;

    let parsed;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return null;
    }

    const { labelType, value, warehouse_id: warehouseId } = parsed || {};
    if (!validScan(labelType, value)) return null;
    return labelType === 'bin' && UUID_RE.test(warehouseId || '')
        ? { type: labelType, value, warehouseId }
        : { type: labelType, value };
};

/**
 * The `scan` URL param for a scanned label, `<type>:<value>`, or `bin@<warehouseId>:<value>` for a
 * bin label that names its warehouse.
 *
 * @param {ScanTarget} scan
 * @returns {string}
 */
export const encodeScanParam = ({ type, value, warehouseId }) =>
    warehouseId ? `${type}@${warehouseId}:${value}` : `${type}:${value}`;

/**
 * Reads the `scan` URL param back. A hand-edited or stale value that no longer fits a label is
 * ignored rather than sent to Hasura, where a malformed uuid would fail the whole query.
 *
 * @param {string|null} param
 * @returns {ScanTarget|null}
 */
export const decodeScanParam = (param) => {
    const idx = (param || '').indexOf(':');
    if (idx < 1) return null;
    const [type, warehouseId] = param.slice(0, idx).split('@');
    const value = param.slice(idx + 1);
    if (!validScan(type, value)) return null;
    if (warehouseId === undefined) return { type, value };
    return type === 'bin' && UUID_RE.test(warehouseId) ? { type, value, warehouseId } : null;
};

/**
 * Updates the page's URL params for a scan: a scan replaces the search term, and null removes the
 * scan. Changes `params` in place and returns it, as `setSearchParams` updaters do.
 *
 * @param {URLSearchParams} params The current URL params.
 * @param {ScanTarget|null} scan The scanned label, or null to clear the scan.
 * @returns {URLSearchParams}
 */
export const applyScanParam = (params, scan) => {
    if (!scan) {
        params.delete('scan');
        return params;
    }
    params.delete('searchTerm');
    params.set('scan', encodeScanParam(scan));
    return params;
};

/**
 * Chip text naming a scanned label. Ids are shortened to their first block; bin labels are
 * already readable.
 *
 * @param {ScanTarget} scan
 * @returns {string}
 */
export const scanChipLabel = ({ type, value }) => {
    if (type === 'item') return `Item label · ${value.slice(0, 8)}`;
    if (type === 'pallet') return `Pallet label · ${value.slice(0, 8)}`;
    return `Bin · ${value}`;
};

const byLocationLabel = (a, b) => a.localeCompare(b, undefined, { numeric: true });

/**
 * Where one item sits in the warehouse: its pallet's bin or pool, or '' when it has none or the
 * pallet has been picked up.
 *
 * @param {object} item Item with its `pallet`.
 * @returns {string}
 */
export const itemBinPoolLocation = (item) => {
    const pallet = item?.pallet;
    if (!pallet?.warehouse_location || pallet.warehouse_status === PALLET_STATUSES.PICKED_UP) return '';
    return pallet.warehouse_location;
};

/**
 * Every bin or pool an order's freight sits in, distinct and sorted, joined with ", ".
 *
 * @param {object} order Order with `itemsByOrderId[].pallet`.
 * @returns {string} The list, or '' when nothing is in the warehouse.
 */
export const binPoolLocations = (order) =>
    [...new Set((order?.itemsByOrderId || []).map(itemBinPoolLocation).filter(Boolean))]
        .sort(byLocationLabel)
        .join(', ');

/**
 * CSV value for Bin/Pool Location. A one-row-per-item export gives each row its own item's
 * location; otherwise the row carries the whole order's list.
 *
 * @param {{ order: object, item?: object, itemRows?: boolean }} row An export row.
 * @returns {string}
 */
export const binPoolLocationExport = ({ order, item, itemRows }) =>
    itemRows ? itemBinPoolLocation(item) : binPoolLocations(order);

/**
 * The URL param for a multi-select filter's picked values, such as `binLocations`. Stored as JSON
 * because values like custom bin names can contain commas.
 *
 * @param {string[]} values
 * @returns {string|null} The param, or null to remove it.
 */
export const encodeListParam = (values) => (Array.isArray(values) && values.length ? JSON.stringify(values) : null);

/**
 * Reads a multi-select filter's URL param back into a list. Anything unreadable is an empty list.
 *
 * @param {string|null} param
 * @returns {string[]}
 */
export const decodeListParam = (param) => {
    if (!param) return [];
    try {
        const parsed = JSON.parse(param);
        return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string' && v) : [];
    } catch {
        return [];
    }
};

/**
 * Bin and pool options for the Bin/Pool Location filter, one per stored location. The value and
 * label are the location as pallets store it. When the pallets span more than one warehouse,
 * `secondary` names the warehouses that use that location, where the viewer can see the warehouse;
 * the filter shows it muted beside the label.
 *
 * @param {Array<{ warehouse_id?: string, warehouse_location: string, warehouse?: { location_name?: string } }>} pallets
 *   One row per location and warehouse.
 * @returns {Array<{ value: string, label: string, secondary?: string }>}
 */
export const binLocationOptions = (pallets) => {
    const list = pallets || [];
    const namesByBin = new Map();
    list.forEach(({ warehouse_location, warehouse }) => {
        if (!warehouse_location) return;
        const names = namesByBin.get(warehouse_location) || [];
        const name = warehouse?.location_name?.trim();
        if (name && !names.includes(name)) names.push(name);
        namesByBin.set(warehouse_location, names);
    });

    const showWarehouse = new Set(list.map(({ warehouse_id }) => warehouse_id || null)).size > 1;
    return [...namesByBin.keys()].sort(byLocationLabel).map((value) => {
        const names = namesByBin.get(value);
        return showWarehouse && names.length
            ? { value, label: value, secondary: names.join(', ') }
            : { value, label: value };
    });
};

/**
 * Bin filter options with every picked bin included, so a picked bin that no longer holds freight,
 * or whose options haven't loaded yet, still shows as picked and isn't dropped when the user edits
 * the filter.
 *
 * @param {Array<{ value: string, label: string }>} options From binLocationOptions.
 * @param {string[]} [picked] The bins in the filter.
 * @returns {Array<{ value: string, label: string, secondary?: string }>}
 */
export const withPickedBinOptions = (options, picked) => {
    const known = new Set(options.map((option) => option.value));
    const missing = (picked || []).filter((value) => !known.has(value));
    return missing.length ? [...options, ...missing.map((value) => ({ value, label: value }))] : options;
};
